import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Alert, Box, SwipeableDrawer } from '@mui/material';
import clsx from 'clsx';
import { MapContext } from 'components/maps/MapContext';
import Search from 'components/Search';
import { BackButtonContext, SearchContext } from 'contexts';
import { VisibilityContext } from 'contexts/VisibilityContext';
import L from 'leaflet';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useHistory, useLocation } from 'react-router-dom';

import useLayersToolbarStyles from '../styles';
import Breadcrumbs from './Breadcrumbs';
import Controls from './Controls';
import useEntitiesDrawerStyles from './styles';
import { EntitiesDrawerProps } from './types';
import ClearSelectedEntitiesButton from './ClearSelectedEntitesButton';

const DRAWER_BLEEDING = 28;

const EntitiesDrawer: React.FunctionComponent<EntitiesDrawerProps> = ({ children }) => {
  const drawerClasses = useLayersToolbarStyles();
  const { t } = useTranslation('maps');
  const classes = useEntitiesDrawerStyles();
  const { visible } = useContext(VisibilityContext);
  const { push } = useHistory();

  const { selectedEntities, hasSelectedEntities, isEntitiesOpen, openEntities, toggleEntities } =
    useContext(MapContext);
  const { pathname } = useLocation();

  useEffect(
    () => {
      if (!isEntitiesOpen && pathname !== '/' && !pathname.includes('mapConfigs')) {
        openEntities();
      }
    },
    // only do this on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, openEntities]
  );

  useEffect(() => {
    hasSelectedEntities && openEntities();
  }, [hasSelectedEntities, selectedEntities, openEntities]);

  const [searchValue, setSearchValue] = useState('');
  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const { isShown } = useContext(BackButtonContext);

  const renderButton = useCallback(() => {
    return (
      <Box
        className={classes.drawerPullerContainer}
        sx={{ left: -DRAWER_BLEEDING }}
        onClick={() => {
          toggleEntities();
          if (isEntitiesOpen) {
            push('/');
          }
        }}
      >
        <div className={classes.drawerPuller}>
          {isEntitiesOpen ? <ChevronRight /> : <ChevronLeft />}
        </div>
      </Box>
    );
  }, [classes.drawerPullerContainer, classes.drawerPuller, isEntitiesOpen, toggleEntities, push]);

  return (
    <SwipeableDrawer
      anchor='right'
      open={isEntitiesOpen}
      onClose={() => toggleEntities()}
      onOpen={() => toggleEntities()}
      swipeAreaWidth={DRAWER_BLEEDING}
      disableSwipeToOpen={false}
      ModalProps={{
        disablePortal: true,
      }}
      hideBackdrop
      variant='persistent'
      PaperProps={{
        className: clsx(drawerClasses.entityDrawer, {
          [classes.defaultTranslateX]: !isEntitiesOpen,
        }),
        id: 'EntitiesDrawer',
      }}
      onWheel={(event) => {
        const pathArray = pathname.split('/');
        const isCameraEntityView = pathArray.length >= 3 && pathArray[1] === 'mapCameras';
        !isCameraEntityView && L.DomEvent.stopPropagation(event as unknown as Event);
      }}
    >
      {renderButton()}
      <div className={classes.drawerContent}>
        <Route path='/' exact>
          <Search
            value={searchValue}
            onChange={onSearchChange}
            InputProps={{
              endAdornment: (
                <>
                  <ClearSelectedEntitiesButton />
                  <Controls />
                </>
              ),
            }}
            sx={{ width: '100%' }}
          />
        </Route>
        <SearchContext.Provider value={searchValue}>
          <div className={clsx(classes.wrapper, { [classes.allWrapper]: pathname === '/' })}>
            <Breadcrumbs wasShown={isShown} isShown={pathname !== '/'} />
            {isEntitiesOpen ? children : null}
            {visible.length === 0 && !isShown && (
              <Alert severity='info'>{t('noSearchResults')}</Alert>
            )}
          </div>
        </SearchContext.Provider>
      </div>
    </SwipeableDrawer>
  );
};

export default memo(EntitiesDrawer) as typeof EntitiesDrawer;
